import { useEffect, useMemo, useState } from "react";
import { X, Search, SlidersHorizontal, RefreshCw } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList
} from "recharts";
import { BASE_URL } from "../../../config";
import PeriodFilterBar, { resolvePeriodRange, defaultPeriodFilterValue } from "../filters/PeriodFilterBar";
import { formatAppIntlLabel, getAppDisplayTimezone } from "../../../utils/appDateTime";
import ShiftDetailModal from "../../ShiftDetailModal";
import "../../ShiftDetailModal.css";

const TYPE_COLOR = { filled: "#90E58C", unfilled: "#3b82f6", total: "#6366f1", cancelled: "#f97316" };
const TYPE_LABEL = { filled: "Filled", unfilled: "Unfilled", total: "Total", cancelled: "Cancelled" };
// The detail-level mini-summary chart contrasts two counts per type: Filled/Unfilled pages show
// the existing Filled-vs-Unfilled breakdown (unchanged); Total/Cancelled pages show Total vs
// Cancelled instead, since Cancelled sits outside Total rather than being a slice of it (see
// dashboardRoutes.js) -- these aren't a "sum to 100%" breakdown there, just two related counts
// shown together.
const SUMMARY_PAIR_BY_TYPE = {
  filled: [{ key: "filled", name: "Filled Orders", color: TYPE_COLOR.filled }, { key: "unfilled", name: "Unfilled Orders", color: TYPE_COLOR.unfilled }],
  unfilled: [{ key: "filled", name: "Filled Orders", color: TYPE_COLOR.filled }, { key: "unfilled", name: "Unfilled Orders", color: TYPE_COLOR.unfilled }],
  total: [{ key: "total", name: "Total Orders", color: TYPE_COLOR.total }, { key: "cancelled", name: "Cancelled Orders", color: TYPE_COLOR.cancelled }],
  cancelled: [{ key: "total", name: "Total Orders", color: TYPE_COLOR.total }, { key: "cancelled", name: "Cancelled Orders", color: TYPE_COLOR.cancelled }],
};
const PAGE_SIZE = 25;
const BRAND_RED = "#DC1F00";
const SORT_LABELS = { date: "Schedule", staff: "Staff", position: "Position" };

// Below this pixel width, a white "insideRight" label doesn't fit inside the bar and
// becomes invisible (white-on-white past the bar's edge) -- render it outside the bar
// in a dark, readable color instead. Applies to any small count (division/client bars,
// the filled-vs-unfilled mini comparison bars).
const MIN_INSIDE_LABEL_WIDTH = 24;
const renderBarCountLabel = ({ x, y, width, height, value }) => {
  const insideFits = width >= MIN_INSIDE_LABEL_WIDTH;
  return (
    <text
      x={insideFits ? x + width - 6 : x + width + 6}
      y={y + height / 2}
      dy={4}
      textAnchor={insideFits ? "end" : "start"}
      fontSize={12}
      fontWeight={600}
      fill={insideFits ? "#ffffff" : "#374151"}
    >
      {value}
    </text>
  );
};

// Per-row status badge. Filled/Unfilled pages are homogeneous (every row already matches that
// one status) so the existing type-wide logic is kept as-is. Total pages mix filled and unfilled
// rows together, so that row's own `isFilled` flag (from the backend) decides its badge instead
// of assuming one status for the whole page. Cancelled pages are homogeneous the other way.
const getRowStatus = (type, row) => {
  if (type === "filled" || (type === "total" && row.isFilled)) {
    return { label: "Filled", style: { backgroundColor: 'rgba(144,229,140,0.3)', color: '#166534' } };
  }
  if (type === "cancelled") {
    return { label: "Cancelled", style: { backgroundColor: 'rgba(249,115,22,0.2)', color: '#9a3412' } };
  }
  // Remaining cases: type === 'unfilled', or type === 'total' with isFilled === false.
  const isDeclined = row.shiftState === "declined";
  return isDeclined
    ? { label: "Unfilled", style: { backgroundColor: 'rgba(245,158,11,0.2)', color: '#92400e' } }
    : { label: "Unassigned", style: { backgroundColor: '#e5e7eb', color: '#374151' } };
};

// Formats the real start/end + unpaid-break-minutes fields into a compact "Schedule" cell
// (date range + computed duration) — nothing here is fabricated, all derived from real API data.
const formatScheduleCell = (startIso, endIso, unpaidBreakMinutes) => {
  if (!startIso || !endIso) return { range: "", duration: "" };
  const tz = getAppDisplayTimezone();
  const start = new Date(startIso);
  const end = new Date(endIso);
  const startDay = formatAppIntlLabel(start, { month: "short", day: "numeric", timeZone: tz });
  const endDay = formatAppIntlLabel(end, { month: "short", day: "numeric", timeZone: tz });
  const startTime = formatAppIntlLabel(start, { hour: "numeric", minute: "2-digit", timeZone: tz });
  const endTime = formatAppIntlLabel(end, { hour: "numeric", minute: "2-digit", timeZone: tz });
  const range = startDay === endDay
    ? `${startDay} · ${startTime} - ${endTime}`
    : `${startDay}, ${startTime} - ${endDay}, ${endTime}`;
  const totalMinutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000) - (unpaidBreakMinutes || 0));
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  const duration = unpaidBreakMinutes > 0
    ? `${h}h ${m}m after ${Math.floor(unpaidBreakMinutes / 60)}h${unpaidBreakMinutes % 60 ? ` ${unpaidBreakMinutes % 60}m` : ""} break`
    : `${h}h ${m}m`;
  return { range, duration };
};

/**
 * Shared 3-level drill-down (division -> client -> single-client detail) for the
 * Order Allocation Rate card. `type` picks color/copy/data field; the flow, layout,
 * and behavior are otherwise identical between Filled and Unfilled per the Figma mockup.
 */
export default function OrderDrilldownFlow({ type, onClose, getAuthHeaders, initialPeriodFilter }) {
  const color = TYPE_COLOR[type];
  const label = TYPE_LABEL[type];

  const [periodFilter, setPeriodFilter] = useState(initialPeriodFilter || defaultPeriodFilterValue());
  const periodRange = resolvePeriodRange(periodFilter);

  const [level, setLevel] = useState("divisions"); // 'divisions' | 'clients' | 'detail'
  const [selectedDivision, setSelectedDivision] = useState(null); // { id, name }
  const [selectedClient, setSelectedClient] = useState(null); // { id, name }

  const [divisionRows, setDivisionRows] = useState([]);
  const [clientRows, setClientRows] = useState([]);
  const [detail, setDetail] = useState({ summary: { filled: 0, unfilled: 0, total: 0, cancelled: 0 }, rows: [], pagination: { page: 1, pageSize: PAGE_SIZE, total: 0 } });

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [sortDir, setSortDir] = useState("asc");
  const [refreshTick, setRefreshTick] = useState(0);
  const [selectedShiftId, setSelectedShiftId] = useState(null);

  const authHeaders = useMemo(() => ({ "Content-Type": "application/json", ...getAuthHeaders() }), [getAuthHeaders]);

  // Level 1: divisions
  useEffect(() => {
    if (level !== "divisions") return;
    const params = new URLSearchParams({ type, startDate: periodRange.startDate, endDate: periodRange.endDate });
    fetch(`${BASE_URL}/api/dashboard/orders-by-division?${params}`, { credentials: "include", headers: authHeaders })
      .then((res) => res.json())
      .then((data) => setDivisionRows(data.success ? data.data : []))
      .catch(() => setDivisionRows([]));
  }, [level, type, periodRange.startDate, periodRange.endDate, authHeaders]);

  // Level 2: clients within selected division
  useEffect(() => {
    if (level !== "clients" || !selectedDivision) return;
    const params = new URLSearchParams({ type, division_id: selectedDivision.id, startDate: periodRange.startDate, endDate: periodRange.endDate });
    fetch(`${BASE_URL}/api/dashboard/orders-by-client?${params}`, { credentials: "include", headers: authHeaders })
      .then((res) => res.json())
      .then((data) => setClientRows(data.success ? data.data : []))
      .catch(() => setClientRows([]));
  }, [level, type, selectedDivision, periodRange.startDate, periodRange.endDate, authHeaders]);

  // Level 3: single-client detail (summary + paginated shift list)
  useEffect(() => {
    if (level !== "detail" || !selectedDivision || !selectedClient) return;
    const params = new URLSearchParams({
      type,
      division_id: selectedDivision.id,
      client_id: selectedClient.id,
      startDate: periodRange.startDate,
      endDate: periodRange.endDate,
      page,
      pageSize: PAGE_SIZE,
      sortBy,
      sortDir,
    });
    if (search) params.append("search", search);
    fetch(`${BASE_URL}/api/dashboard/orders-detail?${params}`, { credentials: "include", headers: authHeaders })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setDetail(data.data);
      })
      .catch(() => {});
  }, [level, type, selectedDivision, selectedClient, periodRange.startDate, periodRange.endDate, page, search, sortBy, sortDir, refreshTick, authHeaders]);

  const goToDivisions = () => {
    setLevel("divisions");
    setSelectedDivision(null);
    setSelectedClient(null);
  };
  const goToClients = () => {
    setLevel("clients");
    setSelectedClient(null);
    setPage(1);
    setSearch("");
  };
  const openDivision = (row) => {
    setSelectedDivision({ id: row.divisionId, name: row.divisionName });
    setLevel("clients");
  };
  const openClient = (row) => {
    setSelectedClient({ id: row.clientId, name: row.clientName });
    setPage(1);
    setSearch("");
    setSortBy("date");
    setSortDir("asc");
    setLevel("detail");
  };
  const toggleSort = (column) => {
    if (sortBy === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
    setPage(1);
  };

  const backLink = level === "divisions"
    ? { text: "Back to Dashboard", onClick: onClose }
    : level === "clients"
      ? { text: `Back to Divisions with ${label} Orders`, onClick: goToDivisions }
      : { text: `Back to ${label} Orders at ${selectedDivision?.name || ""}`, onClick: goToClients };

  const title = level === "divisions"
    ? `Divisions with ${label} Orders`
    : level === "clients"
      ? `${label} Orders at ${selectedDivision?.name || ""}`
      : `${selectedClient?.name || ""} ${label} Orders`;

  const subtitle = level === "divisions"
    ? "Division-wise orders"
    : level === "clients"
      ? "Client-wise orders"
      : null;

  const chartRows = level === "divisions"
    ? divisionRows.map((r) => ({ name: r.divisionName, count: r.count, onClick: () => openDivision(r) }))
    : level === "clients"
      ? clientRows.map((r) => ({ name: r.clientName, count: r.count, onClick: () => openClient(r) }))
      : [];

  const totalPages = Math.max(1, Math.ceil((detail.pagination.total || 0) / PAGE_SIZE));

  const pageNumbers = useMemo(() => {
    const maxShown = 5;
    let start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + maxShown - 1);
    start = Math.max(1, end - maxShown + 1);
    const nums = [];
    for (let p = start; p <= end; p++) nums.push(p);
    return nums;
  }, [page, totalPages]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className={`bg-white rounded-lg shadow-xl w-full ${level === "detail" ? "max-w-3xl" : "max-w-xl"} max-h-[92vh] overflow-y-auto p-6 flex flex-col`}>
        <div className="flex items-center justify-between pb-3 border-b border-gray-200">
          <button type="button" onClick={backLink.onClick} className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700">
            &larr; {backLink.text}
          </button>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 pt-4 pb-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
          <PeriodFilterBar value={periodFilter} onChange={setPeriodFilter} showDivisionFilter={false} />
        </div>

        {level !== "detail" && (
          <div className="mt-4 border border-gray-200 rounded-lg p-4 flex-1">
            <h3 className="text-xs font-medium text-gray-500 mb-2">{subtitle || `${label.toLowerCase()}-wise orders`}</h3>
            <ResponsiveContainer width="100%" height={Math.max(220, chartRows.length * 46)}>
              <BarChart data={chartRows} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }} barSize={16}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                <XAxis type="number" axisLine={{ stroke: '#e5e7eb' }} tickLine={false} tick={{ fontSize: 11, fill: '#6b7280' }} />
                <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#374151' }} width={140} />
                <Tooltip />
                <Bar dataKey="count" radius={[4, 4, 4, 4]} onClick={(data) => (data?.payload?.onClick || data?.onClick)?.()} cursor="pointer">
                  {chartRows.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={color} fillOpacity={index % 2 === 0 ? 1 : 0.5} cursor="pointer" />
                  ))}
                  <LabelList dataKey="count" content={renderBarCountLabel} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {level === "detail" && (
          <div className="mt-4 flex-1 flex flex-col min-h-0">
            {/* Summary mini comparison chart, in its own bordered container. Filled/Unfilled pages
                contrast those two counts (unchanged); Total/Cancelled pages contrast Total vs
                Cancelled instead -- see SUMMARY_PAIR_BY_TYPE above. */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <h3 className="text-xs font-medium text-gray-500 mb-2">{selectedClient?.name} Orders</h3>
              <ResponsiveContainer width="100%" height={110}>
                <BarChart
                  data={SUMMARY_PAIR_BY_TYPE[type].map((s) => ({ name: s.name, value: detail.summary[s.key], fill: s.color }))}
                  layout="vertical"
                  margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                  barSize={16}
                >
                  <XAxis type="number" axisLine={{ stroke: '#e5e7eb' }} tickLine={false} tick={{ fontSize: 11, fill: '#6b7280' }} />
                  <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#374151' }} width={100} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[4, 4, 4, 4]}>
                    {SUMMARY_PAIR_BY_TYPE[type].map((s, index) => (
                      <Cell key={`summary-cell-${index}`} fill={s.color} />
                    ))}
                    <LabelList dataKey="value" content={renderBarCountLabel} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Orders list, in its own bordered container */}
            <div className="border border-gray-200 rounded-lg p-4 flex-1 flex flex-col min-h-0">
              <h3 className="text-sm font-medium text-gray-700 mb-3">{selectedClient?.name} {label} Orders List</h3>

              <div className="flex items-center gap-2 mb-2">
                <div className="relative flex-1">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    placeholder="Search position or staff..."
                    className="w-full text-sm border border-gray-300 rounded-md pl-8 pr-3 py-1.5 outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#DC1F00]"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                  aria-label="Toggle sort direction"
                  title="Toggle sort direction"
                  className="p-1.5 border border-gray-300 rounded-md text-gray-500 hover:bg-gray-50"
                >
                  <SlidersHorizontal size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => setRefreshTick((t) => t + 1)}
                  aria-label="Refresh"
                  title="Refresh"
                  className="p-1.5 border border-gray-300 rounded-md text-gray-500 hover:bg-gray-50"
                >
                  <RefreshCw size={14} />
                </button>
              </div>
              <div className="text-xs text-gray-500 mb-3">
                Sorted by: <span className="text-red-600 font-medium">{SORT_LABELS[sortBy]}, {sortDir === "asc" ? "Ascending" : "Descending"}</span>
              </div>

              <div className="overflow-auto border border-gray-200 rounded-md flex-1 min-h-0">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr style={{ backgroundColor: BRAND_RED }}>
                      <th className="text-left px-3 py-2 font-medium text-white whitespace-nowrap">Shift</th>
                      <th className="text-left px-3 py-2 font-medium text-white whitespace-nowrap cursor-pointer" onClick={() => toggleSort("date")}>Schedule</th>
                      <th className="text-left px-3 py-2 font-medium text-white whitespace-nowrap">Staff Request</th>
                      <th className="text-left px-3 py-2 font-medium text-white whitespace-nowrap cursor-pointer" onClick={() => toggleSort("staff")}>Staff</th>
                      <th className="text-left px-3 py-2 font-medium text-white whitespace-nowrap">Shift Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.rows.map((row, index) => {
                      const { range, duration } = formatScheduleCell(row.startDate, row.endDate, row.unpaidBreakMinutes);
                      const { label: statusLabel, style: statusStyle } = getRowStatus(type, row);
                      return (
                        <tr key={row.shiftId} style={{ backgroundColor: index % 2 === 1 ? 'rgba(220,31,0,0.05)' : 'transparent' }}>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => setSelectedShiftId(row.shiftId)}
                              className="text-gray-800 hover:text-[#DC1F00] hover:underline"
                            >
                              {row.shiftId}
                            </button>
                          </td>
                          <td className="px-3 py-2 text-gray-800">
                            <div>{range}</div>
                            <div className="text-xs text-gray-400">{duration}</div>
                          </td>
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{row.staffRequestId ?? "—"}</td>
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{row.staffName || "—"}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span
                              className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
                              style={statusStyle}
                            >
                              {statusLabel}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                    {detail.rows.length === 0 && (
                      <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400">No orders found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
                <span>
                  Showing {detail.rows.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}-{(page - 1) * PAGE_SIZE + detail.rows.length} of {detail.pagination.total}
                </span>
                <div className="flex items-center gap-1">
                  <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-2 py-1 border rounded disabled:opacity-40">Previous</button>
                  {pageNumbers[0] > 1 && (
                    <>
                      <button type="button" onClick={() => setPage(1)} className="px-2 py-1 border rounded">1</button>
                      {pageNumbers[0] > 2 && <span className="px-1">...</span>}
                    </>
                  )}
                  {pageNumbers.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPage(p)}
                      className="px-2 py-1 border rounded"
                      style={p === page ? { backgroundColor: BRAND_RED, borderColor: BRAND_RED, color: '#fff' } : {}}
                    >
                      {p}
                    </button>
                  ))}
                  {pageNumbers[pageNumbers.length - 1] < totalPages && (
                    <>
                      {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && <span className="px-1">...</span>}
                      <button type="button" onClick={() => setPage(totalPages)} className="px-2 py-1 border rounded">{totalPages}</button>
                    </>
                  )}
                  <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-2 py-1 border rounded disabled:opacity-40">Next</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <ShiftDetailModal
        show={Boolean(selectedShiftId)}
        onHide={() => setSelectedShiftId(null)}
        onRefresh={() => setRefreshTick((t) => t + 1)}
        shiftId={selectedShiftId}
      />
    </div>
  );
}
